import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Archive, ArrowRight, RefreshCw } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import ModuleHeader from './ModuleHeader';
import GeneratedReportsPanel from './GeneratedReportsPanel';
import ReportFilters from './ReportFilters';
import { useReporting } from './ReportingProvider';
import { Button } from './ui/button';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody } from './ui/dialog';
import { categoryLabel } from '../lib/tyreReports';

export default function ReportingHeader({ view }) {
  const location = useLocation();
  const context = useReporting();
  const workspace = context.workspaces[view];
  const { filtersChanged, appliedFilters, report, viewedReport, period, returnToLive, refresh } = workspace;
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (new URLSearchParams(location.search).get('report_job')) setOpen(true);
  }, [location.search]);
  const opening = useRef(0);
  const cancelOpening = useRef(null);
  useEffect(() => () => { opening.current += 1; cancelOpening.current?.(); }, []);
  const [openingId, setOpeningId] = useState(null);
  const activeJobs = context.jobs.filter((job) => ['queued', 'running'].includes(job.status)).length;
  const openJob = async (job, target) => {
    const request = ++opening.current;
    cancelOpening.current = context.workspaces[target].cancelResultOpening;
    setOpeningId(job.id);
    const success = await context.workspaces[target].viewGeneratedReport(job);
    if (request !== opening.current) return;
    cancelOpening.current = null;
    setOpeningId(null);
    if (success) { setOpen(false); context.navigate(target); }
  };
  const selectedVehicle = workspace.vehicleOptions.find((item) => String(item.vehicleId) === appliedFilters.vehicleId);
  return <>
    <Helmet><title>{view === 'reports' ? 'Tyre reports' : 'Tyre analytics'} | Retrack FMS</title></Helmet>
    <ModuleHeader eyebrow="Tyre Management" title={view === 'reports' ? 'Reports' : 'Analytics'}
      description={view === 'reports' ? 'Inspect tyre records, trace movements and export the details.' : 'Understand tyre activity, fleet condition and lifetime costs.'}
      actions={<>
        {report && <Button variant="outline" onClick={() => context.transfer(view, view === 'reports' ? 'analytics' : 'reports')}>
          {view === 'reports' ? 'Analyze this report' : 'View records in Reports'} <ArrowRight />
        </Button>}
        <Dialog open={open} onOpenChange={(next) => {
          if (!next) { opening.current += 1; cancelOpening.current?.(); cancelOpening.current = null; setOpeningId(null); }
          setOpen(next);
        }}>
          <DialogTrigger asChild><Button variant="outline"><Archive /> Generated reports{activeJobs ? ` (${activeJobs} active)` : ''}</Button></DialogTrigger>
          <DialogContent side className="left-auto right-0 top-0 h-[100dvh] max-h-[100dvh] w-full max-w-xl translate-x-0 translate-y-0 rounded-none">
            <DialogHeader><DialogTitle>Generated reports</DialogTitle><DialogDescription>Open stored records or analyze a completed snapshot. Processing continues when you leave.</DialogDescription></DialogHeader>
            <DialogBody>
              <GeneratedReportsPanel jobs={context.jobs} loading={context.jobsLoading} error={context.jobsError}
                loadingResultId={openingId} onRefresh={context.loadJobs}
                onView={(job) => openJob(job, 'reports')} onAnalyze={(job) => openJob(job, 'analytics')} />
              {Object.entries(context.workspaces).map(([name, item]) => item.operationError && <p key={name} role="alert" className="text-sm text-red-700">{item.operationError}</p>)}
            </DialogBody>
          </DialogContent>
        </Dialog>
      </>} />
    <ReportFilters workspace={workspace} />
    {workspace.operationError && <p role="alert" className="text-sm text-red-700">{workspace.operationError}</p>}
    {report && <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border-l-4 border-operational-600 bg-operational-50 px-4 py-3" aria-label="Displayed selection">
      <div>
        <p className="text-sm font-bold text-operational-900">{viewedReport?.job ? 'Generated snapshot' : 'Applied selection'} · {period}</p>
        <p className="mt-1 text-xs text-slate-600">
          {selectedVehicle?.vehicle || appliedFilters.vehicleId || 'All vehicles'} · {appliedFilters.status === 'all' ? 'All statuses' : appliedFilters.status.replace(/_/g, ' ')} · {appliedFilters.category === 'all' ? 'All categories' : categoryLabel(appliedFilters.category)}
          {filtersChanged ? ' · Unapplied changes above' : ''}
        </p>
        {viewedReport?.job && <p className="mt-1 text-xs text-slate-600">Viewing a completed generated report. Figures and exports use its stored results.</p>}
      </div>
      {viewedReport?.job ? <Button size="sm" variant="outline" onClick={returnToLive}>Return to live data</Button>
        : view === 'analytics' && <Button size="sm" variant="ghost" onClick={refresh}><RefreshCw /> Refresh</Button>}
    </div>}
  </>;
}
